// Представление «Объекты» — реестр объектов охраны.
//
// Одна строка — одна грамматика (правило 2): название, заказчик, город, пояс и метка
// «закрыт». Над списком — сколько объектов в этой выборке (из total ответа, не из длины
// показанного куска) и на какой момент срез (из generated_at, не с часов браузера).
//
// Правило 11: при смене фильтра строки НЕ исчезают — запрос идёт с keepPreviousData,
// скелет рисуется только когда данных нет даже в кэше прошлой сессии.

import type { ReactNode } from 'react';

import { RiAddLine, RiBuilding4Line } from '@remixicon/react';
import { useNavigate } from 'react-router';

import { ListPane } from '@/layout/ListPane';
import type { SitesQuery } from '@/shared/api/endpoints/org';
import type { SearchState } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { parseMoment } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';
import * as Switch from '@/shared/ui/switch';

import { ListRow, ListSkeleton } from '@/shared/ui/list-row';
import { SearchFilter } from '@/shared/ui/search-filter';
import type { SitesRoute } from '../route';
import { FILTER_ACTIVE, FILTER_CUSTOMER, openNewSiteHref, setFilter, sitesHref } from '../route';
import { useSitesQuery } from '@/shared/org/queries';

const OBJECT_FORMS = ['объект', 'объекта', 'объектов'] as const;

/**
 * Подпись строки объекта: заказчик и город, но только то, что правда что-то говорит.
 *
 * Найдено репетицией 15.09.2026 на перенесённых боевых данных. У объектов, заведённых
 * переносом, имя заказчика совпадает с названием объекта — в книгах заказчика колонка одна, и
 * различать их там нечем. Список из 77 строк читался так: «Губкинский / Губкинский · город не
 * указан», то есть название дважды и приписка, одинаковая у всех семидесяти семи.
 *
 * Правило то же, что у часового пояса строкой выше: значение, повторяющееся в каждой строке и
 * ничего не различающее, это не сведения, а шум. Совпал заказчик с названием — не пишем его;
 * города нет — не пишем «город не указан», потому что это видно и так, а место в строке
 * занято. Город при этом остаётся на месте там, где он ЕСТЬ: там он и различает.
 */
export function подписьОбъекта(
  название: string,
  заказчик: string | null | undefined,
  город: string | null | undefined,
): string | undefined {
  const части: string[] = [];
  if (
    заказчик !== null &&
    заказчик !== undefined &&
    заказчик.trim() !== '' &&
    заказчик !== название
  ) {
    части.push(заказчик);
  }
  if (город !== null && город !== undefined && город.trim() !== '') {
    части.push(город);
  }
  return части.length === 0 ? undefined : части.join(' · ');
}

export function ObjectsList({
  route,
  viewSwitcher,
}: {
  route: SitesRoute;
  viewSwitcher: ReactNode;
}) {
  const navigate = useNavigate();
  const { search, rowId } = route;

  const activeOnly = search.filters[FILTER_ACTIVE] === 'true';
  const customerId = search.filters[FILTER_CUSTOMER];

  const params: SitesQuery = {
    active: activeOnly ? true : undefined,
    customer_id: customerId,
    q: search.q,
  };
  const query = useSitesQuery(params);

  function apply(next: SearchState) {
    // replace: в истории браузера остаётся один шаг на подбор фильтров, а не двадцать.
    navigate(sitesHref('objects', next, rowId), { replace: true });
  }

  const поиск = (
    <SearchFilter
      label='Поиск объекта по названию'
      placeholder='Название объекта'
      value={search.q ?? ''}
      onApply={(value) => apply({ ...search, q: value === '' ? undefined : value })}
    />
  );

  const наложено = [
    activeOnly ? 'только действующие' : null,
    customerId === undefined ? null : 'один заказчик',
  ].filter((имя): имя is string => имя !== null);

  const filters = (
    <>
      <div className='flex items-center justify-between gap-2'>
        <label htmlFor='sites-active-only' className='text-paragraph-sm text-text-sub-600'>
          Только действующие
        </label>
        <Switch.Root
          id='sites-active-only'
          checked={activeOnly}
          onCheckedChange={(checked) =>
            apply(setFilter(search, FILTER_ACTIVE, checked ? 'true' : undefined))
          }
        />
      </div>
      {customerId !== undefined && (
        <button
          type='button'
          onClick={() => apply(setFilter(search, FILTER_CUSTOMER, undefined))}
          className='self-start text-paragraph-xs text-primary-base underline тач:inline-flex тач:min-h-11 тач:items-center'
        >
          Показаны объекты одного заказчика · снять
        </button>
      )}
    </>
  );

  return (
    <ListPane
      caption={query.data === undefined ? 'Объекты' : countLabel(query.data.total, OBJECT_FORMS)}
      sliceAt={parseMoment(query.data?.generated_at)}
      viewSwitcher={viewSwitcher}
      search={поиск}
      filters={filters}
      appliedFilters={наложено}
      width='имена'
    >
      <ScreenState
        data={query.data}
        error={query.error}
        subject='список объектов'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={<ListSkeleton />}
        empty={
          <EmptyState
            icon={RiBuilding4Line}
            title={
              activeOnly || search.q !== undefined || customerId !== undefined
                ? 'Под этот отбор объектов нет'
                : 'Объектов пока нет'
            }
            description={
              activeOnly || search.q !== undefined || customerId !== undefined
                ? 'Список пуст из-за наложенного отбора, а не потому, что объектов нет вовсе. Снимите поиск и фильтр — увидите весь реестр.'
                : 'Реестр объектов пуст: ни один объект ещё не заведён. Начните с заказчика и юрлица — они нужны объекту при создании.'
            }
            action={
              <Button.Root
                variant='primary'
                mode='stroke'
                size='xsmall'
                onClick={() => navigate(openNewSiteHref(route))}
              >
                <Button.Icon as={RiAddLine} />
                Новый объект
              </Button.Root>
            }
          />
        }
      >
        {(data) => (
          <div className='flex flex-col'>
            {data.items.map((site) => (
              <ListRow
                key={site.id}
                to={`${sitesHref('objects', search, site.id)}`}
                selected={site.id === rowId}
                title={site.name}
                // Часового пояса в строке НЕТ. «UTC+5» — наше служебное обозначение, и в
                // списке из 64 объектов оно повторялось 64 раза, ничего не различая: у всех
                // объектов одного города он одинаков, а действий по нему из списка не
                // совершают. Пояс живёт в карточке объекта, где его и задают.
                subtitle={подписьОбъекта(site.name, site.customer_name, site.city)}
                badge={
                  site.is_active ? undefined : (
                    <Badge.Root variant='lighter' color='gray' size='medium'>
                      закрыт
                    </Badge.Root>
                  )
                }
              />
            ))}
            <div className='flex justify-center p-3'>
              <Button.Root
                variant='neutral'
                mode='stroke'
                size='xsmall'
                onClick={() => navigate(openNewSiteHref(route))}
              >
                <Button.Icon as={RiAddLine} />
                Новый объект
              </Button.Root>
            </div>
          </div>
        )}
      </ScreenState>
    </ListPane>
  );
}
